/**
 * Inventory Registry — its own API client.
 *
 * ITS OWN, for the reason SPN's `skipStart.ts` is its own: products share no code between
 * their folders (CLAUDE.md §1.7). The platform's central `api` object carries typed
 * methods per product, and this product alone would add roughly sixty of them — a
 * surface large enough that it belongs beside the screens that call it, not in a file
 * every other product also has to load.
 *
 * The request shape matches the platform's (`src/api.ts:332`): same-origin credentials,
 * JSON in and out, and an error whose message is the server's `error` field so a ported
 * validation string — "Only Draft opportunities can be deleted" — reaches the screen
 * intact rather than being flattened to a status line.
 */

const BASE = '/api/portfolio_definition';

export class PdApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'PdApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = (await response.json()) as { error?: string; message?: string };
      message = body.error ?? body.message ?? message;
    } catch {
      // Non-JSON body — keep the status line.
    }
    throw new PdApiError(message, response.status);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const get = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) });
const put = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) });
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });

/* ------------------------------------------------------------------ *
 * Row shapes. Deliberately loose where the source's own shape is loose:
 * several screens read columns the source added by ALTER over time, and
 * pinning every one here would invent a contract the API does not enforce.
 * ------------------------------------------------------------------ */

export interface PdProject {
  project_id: string;
  project_description: string;
  additional_description: string | null;
  budget: number | null;
  status: 'Created' | 'Active' | 'Complete';
  effective_from_date: string | null;
  effective_to_date: string | null;
  created_by: number | null;
  created_by_name?: string | null;
  methodology: string | null;
  project_owner: string | null;
  /* The scoring framework governing this project's opportunities. Owner-directed,
     2026-08-10 — see lib/frameworkAssignment.ts. `framework_name` is joined for display. */
  framework_id: string | null;
  framework_name?: string | null;
  [column: string]: unknown;
}

export interface PdOpportunity {
  opportunity_id: string;
  project_id: string | null;
  description: string;
  status: string;
  owner_name: string;
  overall_score: number | null;
  recommendation: string | null;
  business_benefit: number | null;
  criticality_score: number | null;
  criticality_label?: string | null;
  framework_id?: string | null;
  framework_relevant?: boolean | null;
  delivery_pathway?: string | null;
  is_mandatory?: number;
  l1_name?: string | null;
  l2_name?: string | null;
  l3_name?: string | null;
  project_description?: string | null;
  /*
    Declared because the CARD view reads them. They already arrive on the row — the index
    signature below made them `unknown`, which is fine for a table cell that stringifies
    whatever it gets and useless for a card that formats money and dates.
  */
  opportunity_budget?: number | null;
  business_process_owner?: string | null;
  sponsor_keystakeholder?: string | null;
  sponsor_keystakeholder_email?: string | null;
  created_date?: string | null;
  initial_discussion_link?: string | null;
  business_process_requirements_link?: string | null;
  technical_requirements_link?: string | null;
  [column: string]: unknown;
}

export interface PdUser {
  user_id: number;
  username: string;
  full_name: string;
  email: string;
  role: string;
}

export interface PdFramework {
  framework_id: string;
  framework_name: string;
  framework_description: string | null;
  is_default: number;
  is_locked: number;
  design_status: string;
  total_components?: number;
  components_with_questions?: number;
  total_questions?: number;
  is_complete?: number;
  opportunity_count?: number;
  scored_count?: number;
  [column: string]: unknown;
}

export interface PdProcessL1 {
  l1_id: number;
  l1_code: string;
  l1_name: string;
  l1_description: string | null;
  display_order: number | null;
  l2_count?: number;
}
export interface PdProcessL2 {
  l2_id: number;
  l1_id: number;
  l2_code: string;
  l2_name: string;
  l2_description: string | null;
  l1_name?: string;
  l3_count?: number;
}
export interface PdProcessL3 {
  l3_id: number;
  l2_id: number;
  l3_code: string;
  l3_name: string;
  l2_name?: string;
  l1_name?: string;
}

/** One rung of a 1..5 rubric. Which optional fields exist depends on the rubric. */
export interface PdAttributeLevel {
  score: number;
  label: string;
  description?: string | null;
  business_definition?: string | null;
  typical_characteristics?: string | null;
  payback_period?: string | null;
  benefit_multiple?: string | null;
  risk_indicators?: string | null;
  color_code?: string | null;
  is_active: number;
  updated_by?: string | null;
}

export interface PdScoringBand {
  id: number;
  min_score: number;
  max_score: number;
  label: string;
  description: string | null;
  recommendations: string | null;
  color_code: string | null;
  matrix_id: number | null;
}

export interface PdScoringQuestion {
  dimension: { id: number; name: string; description: string | null };
  component: { id: number; name: string; weightage: number };
  question: { id: number; text: string; description: string | null };
  answers: { id: number; choice_text: string; score_value: number }[];
  selected_answer_id: number | null;
}

export interface PdPathwayOpportunity {
  opportunity_id: string;
  project_id: string;
  status: string;
  /** Coalesced for DISPLAY — an unchosen opportunity reads as 'B' everywhere else too. */
  delivery_pathway: string;
  /** Whether anybody actually decided. Distinct from the value above; see the apply flow. */
  pathway_chosen: boolean;
  short_desc: string;
  has_delivery_project: boolean;
  has_effort_planning: boolean;
}

/** A row of `user_saved_views`. `is_default` is 1/0, as the source stores it. */
export interface PdSavedView {
  view_id: number;
  view_name: string;
  filter_config: string;
  is_default: number;
  created_at?: string;
  updated_at?: string;
}

export const pdApi = {
  /* --- executive dashboard --- one read for the whole registry --- */
  executive: {
    data: () => get<Record<string, unknown>>('/executive-dashboard/data'),
  },

  /* --- projects --- */
  projects: {
    list: () =>
      get<{
        projects: PdProject[];
        users: PdUser[];
        frameworks: PdFramework[];
        savedViews: unknown[];
        isAdmin: boolean;
      }>('/projects'),
    dashboard: () => get<Record<string, unknown>>('/projects/dashboard'),
    progressStats: () => get<{ success: boolean; data: Record<string, unknown>[] }>('/projects/progress-stats'),
    detail: (id: string) =>
      get<{ project: PdProject; opportunities: PdOpportunity[] }>(`/projects/${encodeURIComponent(id)}`),
    create: (body: unknown) => post<{ success: boolean; project_id: string }>('/projects/create', body),
    update: (id: string, body: unknown) =>
      post<{ success: boolean }>(`/projects/${encodeURIComponent(id)}/update`, body),
    remove: (id: string) => post<{ success: boolean }>(`/projects/${encodeURIComponent(id)}/delete`),
    checkOpportunities: (id: string) =>
      get<{ hasOpportunities: boolean; opportunityCount: number }>(
        `/projects/${encodeURIComponent(id)}/check-opportunities`,
      ),
    administration: () =>
      get<{
        projects: PdProject[];
        users: PdUser[];
        access_records: Record<string, unknown>[];
        grantable_projects: PdProject[];
        isAdmin: boolean;
      }>('/projects/administration/data'),
    grantAccess: (body: unknown) => post<{ success: boolean }>('/projects/access/grant', body),
    revokeAccess: (accessId: number) => del<{ success: boolean }>(`/projects/access/revoke/${accessId}`),
  },

  /* --- opportunities --- */
  /**
   * Saved views — a user's own named filter sets, per screen.
   *
   * `screen_name` is the SOURCE's own key ('opportunities-list', 'projects'), not a new
   * one: rows already in `user_saved_views` from the source app must keep resolving.
   */
  savedViews: {
    list: (screenName: string) =>
      get<{ success: boolean; views: PdSavedView[] }>(
        `/saved-views/${encodeURIComponent(screenName)}`,
      ),
    save: (body: {
      screen_name: string;
      view_name: string;
      filter_config: string;
      is_default: number;
    }) => post<{ success: boolean; view_id?: number; error?: string }>('/saved-views', body),
    remove: (viewId: number) =>
      del<{ success: boolean; error?: string }>(`/saved-views/${viewId}`),
    setDefault: (viewId: number) =>
      put<{ success: boolean; error?: string }>(`/saved-views/${viewId}/default`, {}),
  },

  opportunities: {
    list: () =>
      get<{ opportunities: PdOpportunity[]; projects: { project_id: string; project_description: string }[] }>(
        '/opportunities/view-all',
      ),
    dashboard: () => get<Record<string, unknown>>('/opportunities/dashboard'),
    formData: () => get<Record<string, unknown>>('/opportunities/form-data'),
    detail: (id: string) => get<Record<string, unknown>>(`/opportunities/${encodeURIComponent(id)}`),
    edit: (id: string) => get<Record<string, unknown>>(`/opportunities/${encodeURIComponent(id)}/edit`),
    create: (body: unknown) => post<{ success: boolean; opportunity_id: string }>('/opportunities/create', body),
    update: (id: string, body: unknown) =>
      post<{ success: boolean }>(`/opportunities/${encodeURIComponent(id)}/update`, body),
    remove: (id: string) => post<{ success: boolean }>(`/opportunities/${encodeURIComponent(id)}/delete`),
    l2ByL1: (l1: number) => get<PdProcessL2[]>(`/opportunities/l2-by-l1/${l1}`),
    l3ByL2: (l2: number) => get<PdProcessL3[]>(`/opportunities/l3-by-l2/${l2}`),
    toggleMandatory: (id: string, isMandatory: boolean) =>
      post<{ success: boolean; is_mandatory: number }>(
        `/opportunities/${encodeURIComponent(id)}/toggle-mandatory`,
        { is_mandatory: isMandatory },
      ),
    assignFramework: (id: string, frameworkId: string) =>
      post<{ success: boolean; frameworkName: string }>(
        `/opportunities/${encodeURIComponent(id)}/assign-framework`,
        { framework_id: frameworkId },
      ),
    sendToScoring: (id: string) =>
      post<{ success: boolean }>(`/opportunities/${encodeURIComponent(id)}/send-to-scoring`),
    revertToDraft: (id: string) =>
      post<{ success: boolean }>(`/opportunities/${encodeURIComponent(id)}/revert-to-draft`),
    unlinkProject: (id: string) =>
      post<{ success: boolean }>(`/opportunities/${encodeURIComponent(id)}/unlink-project`),
    bulkAssign: (opportunityIds: string[], projectId: string) =>
      post<{ success: boolean; count: number; projectDescription: string }>('/opportunities/bulk-assign', {
        opportunityIds,
        projectId,
      }),
    notes: (id: string) => get<Record<string, unknown>[]>(`/opportunities/${encodeURIComponent(id)}/notes`),
    addNote: (id: string, text: string) =>
      post<{ success: boolean }>(`/opportunities/${encodeURIComponent(id)}/notes`, { note_text: text }),
    deleteNote: (id: string, noteId: number) =>
      del<{ success: boolean }>(`/opportunities/${encodeURIComponent(id)}/notes/${noteId}`),
  },

  /* --- scoring --- */
  scoring: {
    overview: () => get<{ stats: Record<string, number> }>('/scoring'),
    queue: () => get<{ opportunities: PdOpportunity[]; stats: Record<string, number> }>('/scoring/queue'),
    inProgress: () =>
      get<{ opportunities: PdOpportunity[]; stats: Record<string, number> }>('/scoring/in-progress'),
    completed: () =>
      get<{ opportunities: PdOpportunity[]; stats: Record<string, number> }>('/scoring/completed'),
    form: (id: string) =>
      get<{
        opportunity: PdOpportunity;
        questions: PdScoringQuestion[];
        total_questions: number;
        stats: { answered: number };
      }>(`/scoring/${encodeURIComponent(id)}/form`),
    save: (id: string, answers: Record<string, number>) =>
      post<{ success: boolean }>(`/scoring/${encodeURIComponent(id)}/save`, { answers }),
    /*
     * Two shapes, because a submit does not always score. When the questionnaire is split
     * between owners and somebody else still owes answers, the server returns `pending` with
     * who it is waiting on instead of a score. Typed as optional rather than a union so the
     * caller reads `pending` first and falls through to the scored fields — the same order the
     * server decides them in.
     */
    submit: (id: string, answers: Record<string, number>) =>
      post<{
        success: boolean;
        overall_score: number;
        recommendation: string | null;
        pending?: boolean;
        answered?: number;
        total?: number;
        waitingOn?: string[];
      }>(`/scoring/${encodeURIComponent(id)}/submit`, { answers }),
    /* The scoring audit trail — owner-directed, not a source endpoint. */
    auditTrail: (opportunityId?: string) =>
      get<{ entries: Record<string, unknown>[]; total: number }>(
        `/scoring/audit-trail${opportunityId ? `?opportunity=${encodeURIComponent(opportunityId)}` : ''}`,
      ),
    rescore: (id: string) => post<{ success: boolean }>(`/scoring/${encodeURIComponent(id)}/rescore`),
    heatmap: () => get<Record<string, unknown>>('/scoring/heatmap/data'),
  },

  /* --- delivery pathway --- */
  deliveryPathway: {
    load: () =>
      get<{
        projects: { project_id: string; project_description: string; status: string; opp_count: number }[];
        opportunities: PdPathwayOpportunity[];
      }>('/delivery-pathway'),
    apply: (pathway: 'A' | 'B', opportunityIds: string[]) =>
      post<{
        success: boolean;
        error?: string;
        changed?: number;
        blocked?: { id: string; reason: string }[];
        pathway?: string;
      }>('/delivery-pathway/apply', { pathway, opportunity_ids: opportunityIds }),
  },

  /* --- financial --- */
  financial: {
    configuration: () => get<Record<string, unknown>>('/financial/configuration'),
    saveSettings: (body: unknown) => post<{ success: boolean }>('/financial/configuration/settings', body),
    saveCostType: (body: unknown) => post<{ success: boolean }>('/financial/cost-types', body),
    toggleCostType: (id: number) => post<{ success: boolean }>(`/financial/cost-types/${id}/toggle`),
    deleteCostType: (id: number) => post<{ success: boolean }>(`/financial/cost-types/${id}/delete`),
    scenarioTypes: (activeOnly?: boolean) =>
      get<{ scenarios: Record<string, unknown>[] }>(
        `/financial/scenario-types${activeOnly ? '?activeOnly=true' : ''}`,
      ),
    createScenarioType: (body: unknown) => post<{ success: boolean }>('/financial/scenario-types', body),
    updateScenarioType: (id: number, body: unknown) =>
      put<{ success: boolean }>(`/financial/scenario-types/${id}`, body),
    deleteScenarioType: (id: number) => del<{ success: boolean }>(`/financial/scenario-types/${id}`),
    costScenarios: (params: { context?: 'MVP' | 'NPV'; project?: string; opportunity?: string; scenario?: string }) =>
      get<Record<string, unknown>>(`/financial/cost-scenarios?${new URLSearchParams(
        Object.entries(params).filter(([, v]) => v) as [string, string][],
      ).toString()}`),
    summary: (opportunity: string, version?: number) =>
      get<Record<string, unknown>>(
        `/financial/summary?opportunity=${encodeURIComponent(opportunity)}${version ? `&version=${version}` : ''}`,
      ),
    saveModel: (opportunityId: string, body: unknown) =>
      post<{ success: boolean }>(`/financial/model/${encodeURIComponent(opportunityId)}`, body),
    addCost: (opportunityId: string, body: unknown) =>
      post<{ success: boolean }>(`/financial/costs/${encodeURIComponent(opportunityId)}`, body),
    updateCost: (opportunityId: string, costId: number, body: unknown) =>
      post<{ success: boolean }>(`/financial/costs/${encodeURIComponent(opportunityId)}/${costId}/update`, body),
    deleteCost: (opportunityId: string, costId: number) =>
      post<{ success: boolean }>(`/financial/costs/${encodeURIComponent(opportunityId)}/${costId}/delete`),
    saveOneTimeCosts: (body: unknown) =>
      post<{ success: boolean; buildCostTotal: number }>('/financial/one-time-costs/save', body),
    versions: (opportunityId: string) =>
      get<{ versions: Record<string, unknown>[] }>(`/financial/versions/${encodeURIComponent(opportunityId)}`),
    setDefaultVersion: (versionId: number) =>
      request<{ success: boolean }>(`/financial/versions/${versionId}/set-default`, { method: 'PATCH' }),
    npvCalculate: (params: Record<string, string>) =>
      get<Record<string, unknown>>(`/financial/npv/calculate?${new URLSearchParams(params).toString()}`),
    npvConfig: (opportunity: string, version: number) =>
      get<Record<string, unknown>>(
        `/financial/npv/config?opportunity=${encodeURIComponent(opportunity)}&version=${version}`,
      ),
    saveNpvConfig: (body: unknown) =>
      post<{ success: boolean; npvResult: Record<string, unknown> }>('/financial/npv/config/save', body),
    npvComparison: (opportunity: string, discountRate?: number, horizonYears?: number) =>
      get<{ success: boolean; comparisonData: Record<string, unknown>[] }>(
        `/financial/npv/comparison?opportunity=${encodeURIComponent(opportunity)}` +
          (discountRate ? `&discount_rate=${discountRate}` : '') +
          (horizonYears ? `&horizon_years=${horizonYears}` : ''),
      ),
    saveNpvParameters: (opportunityId: string, body: unknown) =>
      put<{ success: boolean }>(`/financial/npv-parameters/${encodeURIComponent(opportunityId)}`, body),
  },

  /* --- optimizer --- */
  optimizer: {
    projects: () => get<{ success: boolean; projects: Record<string, unknown>[] }>('/optimizer/projects'),
    optimize: (body: unknown) => post<Record<string, unknown>>('/optimizer/optimize', body),
    saveScenario: (body: unknown) =>
      post<{ success: boolean; scenarioId: number; message: string }>('/optimizer/scenarios', body),
    scenarios: () => get<{ success: boolean; scenarios: Record<string, unknown>[] }>('/optimizer/scenarios'),
    scenario: (id: number) => get<{ success: boolean; scenario: Record<string, unknown> }>(`/optimizer/scenarios/${id}`),
    deleteScenario: (id: number) => del<{ success: boolean }>(`/optimizer/scenarios/${id}`),
    cleanup: () => post<{ success: boolean; deletedCount: number; message: string }>('/optimizer/scenarios/cleanup'),
  },

  /* --- settings --- */
  settings: {
    processHierarchy: () =>
      get<{ l1_processes: PdProcessL1[]; l2_processes: PdProcessL2[]; l3_processes: PdProcessL3[] }>(
        '/settings/process-hierarchy',
      ),
    createL1: (body: unknown) => post<{ success: boolean; message: string }>('/settings/process-hierarchy/l1', body),
    updateL1: (id: number, body: unknown) =>
      post<{ success: boolean; message: string }>(`/settings/process-hierarchy/l1/${id}/update`, body),
    deleteL1: (id: number) => post<{ success: boolean; message: string }>(`/settings/process-hierarchy/l1/${id}/delete`),
    createL2: (body: unknown) => post<{ success: boolean; message: string }>('/settings/process-hierarchy/l2', body),
    updateL2: (id: number, body: unknown) =>
      post<{ success: boolean; message: string }>(`/settings/process-hierarchy/l2/${id}/update`, body),
    deleteL2: (id: number) => post<{ success: boolean; message: string }>(`/settings/process-hierarchy/l2/${id}/delete`),
    createL3: (body: unknown) => post<{ success: boolean; message: string }>('/settings/process-hierarchy/l3', body),
    updateL3: (id: number, body: unknown) =>
      post<{ success: boolean; message: string }>(`/settings/process-hierarchy/l3/${id}/update`, body),
    deleteL3: (id: number) => post<{ success: boolean; message: string }>(`/settings/process-hierarchy/l3/${id}/delete`),

    frameworks: () => get<{ frameworks: PdFramework[] }>('/settings/frameworks'),
    createFramework: (body: unknown) => post<{ success: boolean; framework_id: string }>('/settings/frameworks', body),
    cloneFramework: (id: string) =>
      post<{ success: boolean; framework_id: string; framework_name: string }>(
        `/settings/frameworks/${encodeURIComponent(id)}/clone`,
      ),
    updateFramework: (id: string, body: unknown) =>
      put<{ success: boolean }>(`/settings/frameworks/${encodeURIComponent(id)}`, body),
    deleteFramework: (id: string) => del<{ success: boolean }>(`/settings/frameworks/${encodeURIComponent(id)}`),
    builder: (id: string) =>
      get<{ framework: PdFramework; tree: Record<string, unknown>[]; scoreColors: Record<number, string> }>(
        `/settings/frameworks/${encodeURIComponent(id)}/builder`,
      ),
    designer: (id: string) =>
      get<{
        framework: PdFramework;
        hasBeenScored: boolean;
        dimensions: Record<string, unknown>[];
        unassignedDimensions: Record<string, unknown>[];
        unassignedComponents: Record<string, unknown>[];
      }>(`/settings/frameworks/${encodeURIComponent(id)}/designer`),
    validation: (id: string) => get<Record<string, unknown>>(`/settings/frameworks/${encodeURIComponent(id)}/validation`),
    markDesignComplete: (id: string) =>
      post<{ success: boolean }>(`/settings/frameworks/${encodeURIComponent(id)}/mark-design-complete`),
    revertToInDesign: (id: string) =>
      post<{ success: boolean }>(`/settings/frameworks/${encodeURIComponent(id)}/revert-to-in-design`),
    addDimension: (frameworkId: string, body?: unknown) =>
      post<{ success: boolean; dimension_id: number }>(
        `/settings/frameworks/${encodeURIComponent(frameworkId)}/dimensions`,
        body,
      ),
    removeDimension: (frameworkId: string, dimensionId: number) =>
      del<{ success: boolean }>(
        `/settings/frameworks/${encodeURIComponent(frameworkId)}/dimensions/${dimensionId}`,
      ),
    updateDimension: (id: number, body: unknown) => put<{ success: boolean }>(`/settings/dimensions/${id}`, body),
    addComponent: (frameworkId: string, body: unknown) =>
      post<{ success: boolean; component_id: number }>(
        `/settings/frameworks/${encodeURIComponent(frameworkId)}/components`,
        body,
      ),
    removeComponent: (frameworkId: string, componentId: number) =>
      del<{ success: boolean }>(
        `/settings/frameworks/${encodeURIComponent(frameworkId)}/components/${componentId}`,
      ),
    updateComponent: (id: number, body: unknown) => put<{ success: boolean }>(`/settings/components/${id}`, body),
    addQuestion: (body: unknown) => post<{ success: boolean; id: number }>('/settings/questions', body),
    updateQuestion: (id: number, body: unknown) => put<{ success: boolean }>(`/settings/questions/${id}`, body),
    deleteQuestion: (id: number) => del<{ success: boolean }>(`/settings/questions/${id}`),
    addAnswer: (questionId: number, body: unknown) =>
      post<{ success: boolean; id: number }>(`/settings/questions/${questionId}/answers`, body),
    updateAnswer: (id: number, body: unknown) => put<{ success: boolean }>(`/settings/answers/${id}`, body),
    deleteAnswer: (id: number) => del<{ success: boolean }>(`/settings/answers/${id}`),

    scoring: () => get<{ scoring_recommendations: PdScoringBand[]; matrices: Record<string, unknown>[] }>(
      '/settings/scoring',
    ),
    updateScoringBand: (id: number, body: unknown) =>
      post<{ success: boolean }>(`/settings/scoring/${id}/update`, body),
    createScoringBand: (body: unknown) =>
      post<{ success: boolean }>('/settings/scoring-recommendations', body),
    deleteScoringBand: (id: number) =>
      del<{ success: boolean }>(`/settings/scoring-recommendations/${id}`),

    attributes: (key: string) => get<{ levels: PdAttributeLevel[] }>(`/settings/attributes/${key}`),
    updateAttribute: (key: string, score: number, body: unknown) =>
      post<{ success: boolean }>(`/settings/attributes/${key}/${score}/update`, body),
    createAttributeLevel: (key: string, score: number, body: unknown) =>
      post<{ success: boolean }>(`/settings/attributes/${key}/${score}/create`, body),
    deleteAttributeLevel: (key: string, score: number) =>
      del<{ success: boolean }>(`/settings/attributes/${key}/${score}`),
  },

  /* --- quick start --- */
  quickStart: {
    load: (runId?: number) =>
      get<Record<string, unknown>>(`/quick-start${runId ? `?run=${runId}` : ''}`),
    /** Multipart, so it bypasses the JSON helper above. */
    analyze: async (
      files: File[],
      instructions?: string,
      references?: { file: File; note: string }[],
    ) => {
      const form = new FormData();
      for (const f of files) form.append('files', f);
      if (instructions && instructions.trim()) form.append('instructions', instructions.trim());
      /*
       * `ref_name` carries the EXACT filename alongside its note, and the server pairs the two
       * by that name rather than by position. Posting the note without the name would leave
       * the server no choice but to pair on index — which silently mislabels every file if one
       * is rejected. The pair is emitted together, in step, for that reason.
       */
      for (const r of references ?? []) {
        form.append('ref_files', r.file);
        form.append('ref_name', r.file.name);
        form.append('ref_purpose', r.note ?? '');
      }
      const response = await fetch(`${BASE}/quick-start/analyze`, {
        method: 'POST',
        credentials: 'same-origin',
        body: form,
      });
      if (!response.ok) {
        let message = `${response.status} ${response.statusText}`;
        try {
          const body = (await response.json()) as { error?: string };
          message = body.error ?? message;
        } catch {
          /* keep the status line */
        }
        throw new PdApiError(message, response.status);
      }
      return (await response.json()) as { success: boolean; runId: number };
    },
    /**
     * The Analysis browser's slice. Filtering happens on the SERVER — `?run=&projects=&opps=` —
     * so a 90-project proposal is never shipped here just to be narrowed, and a view is a URL.
     */
    analysis: (queryString: string) =>
      get<Record<string, unknown>>(`/quick-start/analysis${queryString ? `?${queryString}` : ''}`),
    /**
     * Polled while a run is analysing. `analyze` returns as soon as the run row exists —
     * the analysis itself continues in the background, because AWS App Runner kills any
     * request at 120 seconds and a real file takes minutes.
     */
    runStatus: (runId: number) =>
      get<{ success: boolean; status: string; error: string | null; has_proposal: boolean }>(
        `/quick-start/run/${runId}/status`,
      ),
    /**
     * Re-order and re-phase a whole work-item list in one write.
     *
     * The WHOLE list is sent, never a single move: the screen renders a grouped view of a flat
     * array, and index arithmetic across that boundary is where an off-by-one silently
     * re-phases the wrong item. The server validates completeness and refuses anything else.
     */
    reorderItems: (
      runId: number,
      pid: string,
      oid: string | null,
      order: { index: number; phase: string }[],
    ) =>
      post<{ success: boolean; count: number }>(`/quick-start/run/${runId}/reorder-items`, {
        pid,
        oid,
        order,
      }),
    generate: (body: unknown) => post<{ success: boolean; result: Record<string, number> }>('/quick-start/generate', body),
    purge: (runId: number) => post<{ success: boolean }>(`/quick-start/run/${runId}/purge`),
    remove: (runId: number) => post<{ success: boolean }>(`/quick-start/run/${runId}/delete`),
  },
};
